import type { ReactNode } from 'react';

import { RouteError, RouteLoading } from '@/components/ui/route-state';
import type { GateableQuery } from '@/components/ui/query-gate';
import { isNotFound } from '@/lib/unwrap';
import { DetailNotFoundPage, type DetailNotFoundProps } from './detail-parts';

/**
 * `QueryGate` for a screen about one record, which has a third outcome: the record is
 * not there. A 404 renders the not-found page with its way back; any other failure the
 * retry panel.
 *
 * Unlike `QueryGate` it gates on `isError`: a detail whose refetch answers 404 has been
 * deleted under the admin, and showing the stale record would invite a decision on it.
 */
export function DetailGate<T>({
  query,
  notFound,
  children,
}: {
  query: GateableQuery<T> & { readonly isError: boolean };
  notFound: DetailNotFoundProps;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <RouteLoading />;
  if (query.isError || query.data === undefined) {
    if (isNotFound(query.error)) return <DetailNotFoundPage {...notFound} />;
    return <RouteError error={query.error} onRetry={query.refetch} />;
  }
  return children(query.data);
}
